import React, { useState, useEffect, useRef } from 'react';
import { Collapse, Table, Typography, Button, Popover, Spin, message as antMessage } from 'antd';
import { DownOutlined, RightOutlined, LeftOutlined } from '@ant-design/icons';
import SimpleTableDisplay from './SimpleTableDisplay';

const { Panel } = Collapse;
const { Text, Title } = Typography;

/**
 * AccordionTableDisplay component for displaying hierarchical data in accordion format with color indicators
 */
const AccordionTableDisplay = ({
  data,
  title = "Accordion Display",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent,
  sessionId,
  project_id,
  agent_key
}) => {
  // State to track which panels are expanded
  const [expandedPanels, setExpandedPanels] = useState([]);
  
  // Use useEffect to update the state after render
  const expandIconRef = useRef(null);

  const [claimData, setClaimData] = useState({});
  const [loadingClaim, setLoadingClaim] = useState({});

  useEffect(() => {
    if (expandIconRef.current !== null) {
      setExpandedPanels(expandIconRef.current);
      expandIconRef.current = null;
    }
  }, []);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      const refreshComponent = () => {
        console.log(`Refreshing AccordionTableDisplay: ${uuid}`);
        setExpandedPanels(prev => [...prev]);
      };
      
      const unregister = registerComponent(refreshComponent);
      return unregister;
    }
  }, [registerComponent, uuid]);

  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };

  // Enhanced Color indicator component with support for lightgreen
  const ColorIndicator = ({ color }) => {
    const colorMap = {
      red: '#ff4d4f',
      green: '#52c41a',
      yellow: '#faad14',
      lightgreen: '#95de64', // Added support for lightgreen
      gray: '#d9d9d9'
    };
    
    return (
      <div
        style={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          backgroundColor: color ? (colorMap[color] || '#d9d9d9') : 'transparent',
          marginRight: '8px',
          display: 'inline-block',
          flexShrink: 0,
          border: color ? 'none' : '1px solid transparent'
        }}
      />
    );
  };

  // Extract accordion data from the data prop
  const getAccordionData = () => {
    if (!data || typeof data !== 'object') return { title: title, items: [] };
    
    // Get the title
    const displayTitle = data.title || title;
    
    // Extract items - collect all objects that have 'header' and 'details'
    let items = [];
    
    // Check if data has an array property (including 'body')
    if (Array.isArray(data.items)) {
      items = data.items;
    } else if (Array.isArray(data.records)) {
      items = data.records;
    } else if (Array.isArray(data.data)) {
      items = data.data;
    } else if (Array.isArray(data.body)) {
      items = data.body;
    } else {
      // If no array property, collect all objects with header and details
      items = Object.keys(data)
        .filter(key => key !== 'title')
        .map(key => data[key])
        .filter(item => item && item.header && item.details);
    }

    return { title: displayTitle, items };
  };

  // Convert details object to table data
  const getTableDataFromDetails = (details, url, item) => {
    if (!details || typeof details !== 'object') return [];
    
    // If details is an array, use the existing logic
    if (Array.isArray(details)) {
      return details.map((detail, index) => {
        return { ...detail, key: index };
      });
    }
    
    // If details is an object, convert to key-value pairs
    const tableData = [];
    Object.entries(details).forEach(([key, value], index) => {
      // Skip color as it's used for the indicator
      if (key === 'Color' || key === 'color') return;
      
      tableData.push({
        key: index,
        field: key,
        value: value,
        color: details.Color || details.color // Pass color for special rendering
      });
    });
    
    // Add URL as the first row if it exists
    if (url) {
      // Extract claim ID from the parent item context
      const claimIdMatch = item?.header?.match(/CLM\d+/);
      const claimId = claimIdMatch ? claimIdMatch[0] : null;
      
      tableData.unshift({
        key: 'url-row',
        field: 'URL',
        value: url,
        isUrl: true,
        claimId: claimId,
        jobId: item.job_id,
        claimDataMemId: item.claim_data_mem_id
      });
    }
        
    return tableData;
  };

  // Format field names for display
  const formatFieldName = (fieldName) => {
    // Convert from camelCase or snake_case to Title Case
    return fieldName
      .replace(/([A-Z])/g, ' $1') // Add space before capital letters
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()) // Capitalize first letter of each word
      .trim();
  };

  // Enhanced render value function with bold formatting support
  const renderValue = (value) => {
    if (value === null || value === undefined) {
      return <Text type="secondary">N/A</Text>;
    }
    
    if (Array.isArray(value)) {
      return (
        <div>
          {value.map((item, idx) => (
            <div key={idx} style={{ marginBottom: idx < value.length - 1 ? '6px' : 0, fontSize: '14px' }}>
              {/* Handle bold formatting for Rule/Result */}
              {typeof item === 'string' && item.includes('**') ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: item
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/^• /, '')
                  }}
                />
              ) : (
                <>• {item}</>
              )}
            </div>
          ))}
        </div>
      );
    }
    
    if (typeof value === 'object') {
      // For nested objects, show as formatted string
      return <Text style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{JSON.stringify(value, null, 2)}</Text>;
    }
    
    // Handle bold formatting for strings
    if (typeof value === 'string' && value.includes('**')) {
      return (
        <span
          style={{ fontSize: '14px' }}
          dangerouslySetInnerHTML={{
            __html: value.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          }}
        />
      );
    }
    
    // For strings and other primitives
    return <Text style={{ fontSize: '14px' }}>{String(value)}</Text>;
  };

  const fetchClaimDetails = async (claimId, url, jobId, claimDataMemId) => {
    try {
      setLoadingClaim(prev => ({ ...prev, [claimId]: true }));

      console.log('??????', project_id)
      
      const urlWithParams = new URL(url);
      urlWithParams.searchParams.append('project_id', project_id);
      urlWithParams.searchParams.append('job_id', jobId);
      urlWithParams.searchParams.append('claim_data_mem_id', claimDataMemId);
      
      const response = await fetch(urlWithParams, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`,
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setClaimData(prev => ({ ...prev, [claimId]: data }));
      setLoadingClaim(prev => ({ ...prev, [claimId]: false }));
    } catch (error) {
      console.error('Error fetching claim details:', error);
      setLoadingClaim(prev => ({ ...prev, [claimId]: false }));
      antMessage.error(`Failed to load claim details: ${error.message}`);
    }
  };

  // Enhanced columns with better spacing and larger fonts
  const getColumnsForObjectDetails = () => {
    return [
      {
        title: 'Field',
        dataIndex: 'field',
        key: 'field',
        width: '25%', // Reduced from 30% to give more space to values
        render: (field) => (
          <Text strong style={{ fontSize: '14px' }}>
            {formatFieldName(field)}
          </Text>
        )
      },
      {
        title: 'Value',
        dataIndex: 'value',
        key: 'value',
        width: '75%', // Increased from 70%
        render: (value, record) => {
          // Special rendering for URL field
          if (record.field === 'URL' || record.isUrl) {
            const claimId = record.claimId || value.split('/').pop();
            const jobId = record.jobId;  
            const claimDataMemId = record.claimDataMemId;  
                      
            return (
              <Popover
                content={
                  <div style={{ width: '800px', maxHeight: '600px', overflow: 'auto' }}>
                    {loadingClaim[claimId] ? (
                      <div style={{ padding: '20px', textAlign: 'center' }}>
                        <Spin size="small" /> Loading claim details...
                      </div>
                    ) : claimData[claimId] ? (
                      <SimpleTableDisplay
                        data={claimData[claimId]}
                        title={`Claim Details: ${claimId}`}
                      />
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center' }}>
                        Click to load claim details
                      </div>
                    )}
                  </div>
                }
                title={`Claim ${claimId} Details`}
                trigger="click"
                placement="right"
                onOpenChange={(visible) => {
                  console.log("??????/. ", value)
                  if (visible && !claimData[claimId] && !loadingClaim[claimId]) {
                    fetchClaimDetails(claimId, value, jobId, claimDataMemId);
                  }
                }}
                overlayStyle={{ maxWidth: '850px' }}
              >
                <a 
                  style={{ 
                    color: '#1890ff', 
                    cursor: 'pointer', 
                    textDecoration: 'underline',
                    fontSize: '14px'
                  }}
                  onClick={(e) => e.preventDefault()}
                >
                  link
                </a>
              </Popover>
            );
          }
          
          // Special rendering for status fields to show colors
          if (record.field === 'Eligibility Status' || record.field === 'Reimbursement Status') {
            // Extract color from status text like "Passed (green)" or "Partially compliant (yellow)"
            const colorMatch = value.match(/\(([^)]+)\)$/);
            if (colorMatch) {
              const color = colorMatch[1];
              const statusText = value.replace(/\s*\([^)]+\)$/, '');
              return (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <ColorIndicator color={color} />
                  <Text strong style={{ fontSize: '14px' }}>{statusText}</Text>
                </div>
              );
            }
          }
          
          // Special rendering for specific fields
          if (record.field === 'Area' || record.field === 'area') {
            return (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <ColorIndicator color={record.color} />
                <Text strong style={{ fontSize: '14px' }}>{value}</Text>
              </div>
            );
          }
          
          if (record.field === 'Outcome' || record.field === 'outcome') {
            const outcomeColor = value === 'Passed' ? '#52c41a' :
                               value === 'Failed' ? '#ff4d4f' :
                               '#000000';
            return <Text strong style={{ color: outcomeColor, fontSize: '14px' }}>{value}</Text>;
          }
          
          return renderValue(value);
        }
      }
    ];
  };

  // Custom expand icon that tracks state
  const customExpandIcon = ({ isActive }) => {
    expandIconRef.current = isActive;
    return isActive ? <DownOutlined /> : <RightOutlined />;
  };

  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;

  const { title: displayTitle, items } = getAccordionData();

  return (
    <div style={{ 
      marginBottom: '4px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px',
      backgroundColor: 'white'
    }}>
      {/* Title */}
      <div style={{ 
        padding: '12px 16px', // Reduced from 16px
        borderBottom: '1px solid #f0f0f0',
        backgroundColor: '#fafafa'
      }}>
        <Title level={4} style={{ margin: 0, fontSize: '18px' }}>{displayTitle}</Title>
      </div>
      
      {/* Accordion Content */}
      <div style={{ padding: '4px' }}> {/* Reduced from 8px */}
        {items.length > 0 ? (
          <Collapse
            expandIcon={customExpandIcon}
            onChange={setExpandedPanels}
            activeKey={expandedPanels}
            bordered={false}
            style={{ backgroundColor: 'transparent' }}
          >
            {items.map((item, index) => {
              const itemColor = item.details?.Color || item.details?.color || item.color;
              
              return (
                <Panel 
                  header={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <ColorIndicator color={itemColor} />
                      <Text strong style={{ fontSize: '16px' }}>
                        {item.header}
                      </Text>
                    </div>
                  } 
                  key={index}
                  style={{ 
                    marginBottom: '4px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px'
                  }}
                >
                  {item.details ? (
                    <Table
                      columns={getColumnsForObjectDetails()}
                      dataSource={getTableDataFromDetails(item.details, item.url, item)}
                      pagination={false}
                      size="small"
                      bordered={false}
                      showHeader={false}
                      style={{ 
                        marginTop: '-4px', // Reduced spacing
                        fontSize: '14px' // Larger font
                      }}
                      // Enhanced row styling for better spacing
                      rowClassName="enhanced-table-row"
                    />
                  ) : (
                    <Text type="secondary">No details available</Text>
                  )}
                </Panel>
              );
            })}
          </Collapse>
        ) : (
          <div style={{ padding: '16px', textAlign: 'center' }}>
            <Text type="secondary">No data available to display</Text>
          </div>
        )}
      </div>
      
      {/* Version navigation */}
      {showVersionNavigation && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
          borderTop: '1px solid #f0f0f0',
          backgroundColor: '#fafafa'
        }}>
          <Button 
            size="small" 
            icon={<LeftOutlined />}
            disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
            onClick={() => {
              const versions = versionInfo.available_versions;
              const currentIndex = versions.indexOf(currentVersion);
              if (currentIndex > 0) {
                handleVersionChange(versions[currentIndex - 1]);
              }
            }}
          >
            Previous
          </Button>
          
          <div>
            Version {currentVersion} of {versionInfo.total_versions}
          </div>
          
          <Button 
            size="small" 
            icon={<RightOutlined />}
            disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
            onClick={() => {
              const versions = versionInfo.available_versions;
              const currentIndex = versions.indexOf(currentVersion);
              if (currentIndex < versions.length - 1) {
                handleVersionChange(versions[currentIndex + 1]);
              }
            }}
          >
            Next
          </Button>
        </div>
      )}
      
      {/* Add custom CSS for enhanced row styling */}
      <style jsx>{`
        .enhanced-table-row td {
          padding: 8px 12px !important;
          font-size: 14px !important;
        }
        
        .enhanced-table-row:hover {
          background-color: #f5f5f5 !important;
        }
      `}</style>
    </div>
  );
};

export default AccordionTableDisplay;